/**
 * useFcmToken — FCM push notification setup hook.
 *
 * On mount (for authenticated users):
 *  1. Requests Notification permission.
 *  2. Reuses the existing PWA service worker (sw.js) for FCM token binding.
 *  3. Fetches the FCM token and POSTs it to the backend.
 *  4. Listens for PUSH_NOTIFICATION messages relayed from the SW and shows
 *     them as Sonner toasts with a chime — independent of token registration.
 *
 * Idempotent — safe to call on every authenticated page render.
 */
'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useNotificationStore } from '@/store/notification-store';
import { useCommandStore } from '@/store/command-store';
import { useTokenIcons } from '@/hooks/use-token-icons';
import { authFetch } from '@/lib/auth-fetch';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { playNotificationChime } from '@/lib/notification-sound';

function extractConfidence(body: string): string | undefined {
  const m = body.match(/\b(HIGH|LOW|NORMAL)\s+confidence/i)
  return m ? m[1].toUpperCase() : undefined
}

// ─── Shared helpers ────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-500/15 text-blue-400',
  'bg-purple-500/15 text-purple-400',
  'bg-orange-500/15 text-orange-400',
  'bg-teal-500/15 text-teal-400',
  'bg-indigo-500/15 text-indigo-400',
  'bg-rose-500/15 text-rose-400',
  'bg-amber-500/15 text-amber-400',
  'bg-cyan-500/15 text-cyan-400',
];

function avatarColor(symbol: string): string {
  const hash = symbol.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// ─── Toast card component ──────────────────────────────────────────────────────
// A proper React component so it can use hooks and re-render when the
// availableCoins store updates (i.e. after the positions page fetch completes).

interface NotificationToastProps {
  toastId: string | number;
  symbol: string;
  body: string;
  direction: string;
  onNavigate: () => void;
}

function NotificationToast({ toastId, symbol, body, direction, onNavigate }: NotificationToastProps) {
  // Live hook reads — re-renders when availableCoins populates or fetchMissing resolves
  const availableCoins = useCommandStore((s) => s.availableCoins);
  const { getIcon } = useTokenIcons();  // re-renders via subscriber when icons arrive

  const displaySymbol = symbol.replace(/USDT$/i, '');
  // DB-backed useTokenIcons takes priority — always has fresh data from 6500+ coins
  const iconUrl = getIcon(symbol) || availableCoins.find((c) => c.symbol === symbol)?.logoUrl || null;

  // Icon resolution is covered by the DB-backed bulk map — no fallback needed.
  const color = avatarColor(displaySymbol);

  const isLong = direction === 'LONG';
  const hasDirection = !!direction;

  return (
    <div
      onClick={() => { onNavigate(); toast.dismiss(toastId); }}
      className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2.5 shadow-lg cursor-pointer w-full hover:bg-muted/30 transition-colors"
    >
      {/* Coin avatar */}
      <div className={cn('relative h-8 w-8 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold', color)}>
        <span className="select-none">{displaySymbol.slice(0, 2).toUpperCase()}</span>
        {iconUrl && (
          <img
            src={iconUrl}
            alt={displaySymbol}
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
      </div>

      {/* Symbol + price */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-foreground block leading-tight">{displaySymbol}</span>
        <span className="text-[11px] text-muted-foreground font-mono leading-tight">
          {(() => { const m = body.match(/Entry\s*\$?([\d,.]+)/i); return m ? `$${m[1]}` : ''; })()}
        </span>
      </div>

      {/* Trend icon */}
      {hasDirection && isLong && (
        <TrendUp className="shrink-0 h-5 w-5 text-emerald-500" weight="bold" />
      )}
      {hasDirection && !isLong && (
        <TrendDown className="shrink-0 h-5 w-5 text-red-500" weight="bold" />
      )}
    </div>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

/** Returns true if a signal's confidence passes the user's filter preference. */
function passesConfidenceFilter(signalConfidence: string | undefined, filter: string): boolean {
  const c = (signalConfidence || '').toUpperCase();
  if (filter === 'high_only') return c === 'HIGH';
  if (filter === 'normal_and_high') return c === 'HIGH' || c === 'NORMAL';
  return true; // 'all'
}

export function useFcmToken(enabled = false, userId: string | null = null) {
  const registered = useRef(false);
  const router = useRouter();
  const setSelectedSymbol = useCommandStore((s) => s.setSelectedSymbol);

  // Cross-path dedup: tracks recently handled signals to suppress WS→polling and
  // WS→FCM foreground duplicates. Key = "SYMBOL|DIRECTION|TAG", TTL = 4 minutes.
  const handledKeys = useRef<Map<string, number>>(new Map());

  // Cached confidence preference — loaded once, stays current without re-renders.
  const confidenceFilterRef = useRef<string>('all');

  // Refs so closures always see the latest values without recreating listeners.
  const routerRef = useRef(router);
  const setSelectedSymbolRef = useRef(setSelectedSymbol);

  useEffect(() => { routerRef.current = router; }, [router]);
  useEffect(() => { setSelectedSymbolRef.current = setSelectedSymbol; }, [setSelectedSymbol]);

  // ── Hydrate notification store from server history on mount ───────────────
  // Loads UserNotification records (breakout, CVD, feed signals) into the
  // Signals widget. Runs once per page load — the store guards against repeat calls.
  useEffect(() => {
    if (!enabled || !userId) return;
    useNotificationStore.getState().loadFromServer();
  }, [enabled, userId]);

  // ── Load user's notification preferences (confidence filter) ───────────────
  // Read from localStorage immediately (written by NotificationSettingsModal on save),
  // then fetch the authoritative value from the backend as a fallback.
  useEffect(() => {
    if (!enabled || !userId) return;

    // Sync from localStorage first — available immediately, written by the modal.
    const stored = localStorage.getItem('algoglide-confidence-filter');
    if (stored) confidenceFilterRef.current = stored;

    // Fetch authoritative value from backend.
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';
    authFetch(`${backendUrl}/api/exchange/notification-preferences/${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.signal_confidence_filter) {
          confidenceFilterRef.current = data.signal_confidence_filter;
          localStorage.setItem('algoglide-confidence-filter', data.signal_confidence_filter);
        }
      })
      .catch(() => {});

    // Keep ref in sync when user changes the setting in the modal.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'algoglide-confidence-filter' && e.newValue) {
        confidenceFilterRef.current = e.newValue;
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [enabled, userId]);

  // ── Shared signal handler (used by both BroadcastChannel and signal WS) ────
  const handleSignal = useRef((data: {
    title?: string; body?: string; url?: string; symbol?: string;
    direction?: string; confidence?: string; tag?: string;
    ai_reasoning?: string; ai_approved?: boolean | null;
  }) => {
    const title = data.title ?? 'Algoglide';
    const body = data.body ?? '';
    const url = data.url ?? '/terminal';
    let symbol = data.symbol ?? '';
    const direction = data.direction ?? '';
    const confidence = data.confidence || extractConfidence(body) || undefined;
    const tag = data.tag || undefined;
    const ai_reasoning = data.ai_reasoning || undefined;
    const ai_approved = data.ai_approved !== undefined ? data.ai_approved : undefined;

    // Cross-path dedup — suppress WS→polling and WS→FCM foreground duplicates.
    // A 4-minute window is long enough to catch the 20s polling lag and near-instant
    // FCM foreground delivery, but short enough to allow genuinely new signals.
    const tagUpper0 = (tag || '').toUpperCase();
    const dedupeKey = `${symbol}|${direction.toUpperCase()}|${tagUpper0}`;
    const nowMs = Date.now();
    const lastSeen = handledKeys.current.get(dedupeKey) ?? 0;
    if (nowMs - lastSeen < 4 * 60 * 1000) {
      return;
    }
    handledKeys.current.set(dedupeKey, nowMs);
    // Prune stale entries to prevent unbounded growth.
    for (const [k, ts] of handledKeys.current.entries()) {
      if (nowMs - ts > 10 * 60 * 1000) handledKeys.current.delete(k);
    }

    // Apply the user's confidence filter to all AI-graded signals.
    // LIQ/WATCH/FEED have their own notification toggles and no confidence rating — skip filter.
    // Everything else (breakout HIGH/NORMAL/LOW, CVD, UNVERIFIED) goes through the filter.
    const noFilterTags = new Set(['LIQ', 'WATCH', 'FEED']);
    const tagUpper = tagUpper0;
    const activeFilter = confidenceFilterRef.current;
    // UNVERIFIED — AI couldn't evaluate, confidence unknown. Only 'all' users receive it.
    if (tagUpper === 'UNVERIFIED' && activeFilter !== 'all') {
      return;
    }
    // For signals that aren't LIQ/WATCH/FEED: apply confidence filter.
    // If confidence is missing/empty, treat as unverified — only 'all' users see it.
    if (!noFilterTags.has(tagUpper) && tagUpper !== 'UNVERIFIED') {
      if (!confidence && activeFilter !== 'all') {
        // No confidence rating — treat as unverified, block for filtered users
        return;
      }
      if (confidence && !passesConfidenceFilter(confidence, activeFilter)) {
        return;
      }
    }

    if (!symbol && title) {
      const match = title.match(/([A-Z0-9]+USDT)\b/i);
      if (match) symbol = match[1].toUpperCase();
    }

    useNotificationStore.getState().add({ title, body, url, symbol, direction, confidence, tag, ai_reasoning, ai_approved });
    if (localStorage.getItem("transaction-sounds") !== "false") {
      playNotificationChime();
    }

    const navigate = () => {
      if (symbol) setSelectedSymbolRef.current(symbol);
      routerRef.current.push(url);
    };

    toast.custom((toastId) => (
      <NotificationToast
        toastId={toastId}
        symbol={symbol}
        body={body}
        direction={direction}
        onNavigate={navigate}
      />
    ), { duration: 8000, className: '!w-[420px] !max-w-[420px]' });
  });

  // ── Signal log polling — fallback for approved signals not caught by WS ──
  // Runs globally (hook is always mounted via FcmRegistrar) so toasts and
  // chimes fire even when the signals widget is closed.
  // Also re-polls notifications/history every 5 min to recover any feed signals
  // that arrived while the WS was briefly disconnected.
  useEffect(() => {
    if (!enabled || !userId) return;
    if (typeof window === 'undefined') return;

    // Periodic re-sync of server notification history (covers feed signals missed by WS)
    const historyInterval = setInterval(() => {
      useNotificationStore.setState({ serverLoaded: false });
      useNotificationStore.getState().loadFromServer();
    }, 5 * 60 * 1000);

    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';
    const seenUuids = new Set<string>();
    let firstLoad = true;
    let stopped = false;

    const poll = async () => {
      try {
        const filter = confidenceFilterRef.current || 'all';
        const res = await authFetch(
          `${backendUrl}/api/exchange/signal-log?limit=50&confidence_filter=${filter}`
        );
        if (!res.ok || stopped) return;
        const data = await res.json();
        if (!Array.isArray(data)) return;

        if (firstLoad) {
          firstLoad = false;
          data.forEach((e: { uuid: string }) => seenUuids.add(e.uuid));
          // On first load, still surface signals from the last 5 minutes so the
          // Signals feed isn't empty after a page refresh or tab switch.
          const cutoff = Date.now() - 5 * 60 * 1000;
          const recentApproved = data.filter((e: {
            uuid: string; ai_approved: boolean | null; created_at: string | null
          }) => e.ai_approved === true &&
            e.created_at && new Date(e.created_at).getTime() > cutoff
          );
          recentApproved.forEach((sig: {
            uuid: string; symbol: string; direction: string; signal_type: string;
            ai_confidence: string | null; ai_reasoning: string | null;
            tag: string | null; ai_approved: boolean | null;
          }, idx: number) => {
            const base = sig.symbol.replace(/USDT$/i, '');
            const dir = sig.direction?.toUpperCase() === 'LONG' ? 'Long' : 'Short';
            setTimeout(() => {
              if (stopped) return;
              handleSignal.current({
                title: `${base} ${sig.signal_type}`,
                body: `${dir} | ${sig.ai_confidence || 'VERIFIED'} | Entry signal`,
                url: `/terminal/futures/${sig.symbol}`,
                symbol: sig.symbol,
                direction: sig.direction,
                confidence: sig.ai_confidence || undefined,
                tag: sig.tag || sig.signal_type,
                ai_reasoning: sig.ai_reasoning || undefined,
                ai_approved: sig.ai_approved,
              });
            }, idx * 2500);
          });
          return;
        }

        const newApproved = data.filter(
          (e: { uuid: string; ai_approved: boolean | null }) =>
            e.ai_approved === true && !seenUuids.has(e.uuid)
        );
        data.forEach((e: { uuid: string }) => seenUuids.add(e.uuid));

        newApproved.forEach((sig: {
          uuid: string; symbol: string; direction: string; signal_type: string;
          ai_confidence: string | null; ai_reasoning: string | null;
          tag: string | null; ai_approved: boolean | null;
        }, idx: number) => {
          const base = sig.symbol.replace(/USDT$/i, '');
          const dir = sig.direction?.toUpperCase() === 'LONG' ? 'Long' : 'Short';
          setTimeout(() => {
            if (stopped) return;
            handleSignal.current({
              title: `${base} ${sig.signal_type}`,
              body: `${dir} | ${sig.ai_confidence || 'VERIFIED'} | Entry signal`,
              url: `/terminal/futures/${sig.symbol}`,
              symbol: sig.symbol,
              direction: sig.direction,
              confidence: sig.ai_confidence || undefined,
              tag: sig.tag || sig.signal_type,
              ai_reasoning: sig.ai_reasoning || undefined,
              ai_approved: sig.ai_approved,
            });
          }, idx * 2500);
        });
      } catch {
        // non-fatal
      }
    };

    poll();
    const interval = setInterval(poll, 20_000);
    return () => {
      stopped = true;
      clearInterval(interval);
      clearInterval(historyInterval);
    };
  }, [enabled, userId]);

  // ── Signal WebSocket — real-time in-app signal stream ─────────────────────
  // Fires for ALL signal types (CVD, LIQ, breakout, watchlist, feed)
  // regardless of whether the browser tab is active. Complements FCM push.
  useEffect(() => {
    if (!enabled || !userId) return;
    if (typeof window === 'undefined') return;

    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/ws/signals/${userId}`;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'signal') {
            console.log('[Signal WS] Received signal:', data.symbol, data.tag, data.title);
            handleSignal.current(data);
          }
          // "ping" frames are ignored — just keepalive
        } catch { /* ignore malformed */ }
      };

      ws.onopen = () => {
        console.log('[Signal WS] Connected to', wsUrl);
      };

      ws.onclose = () => {
        if (stopped) return;
        // Reconnect after 5s — signal WS must stay alive
        reconnectTimer = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [enabled, userId]);

  // ── BroadcastChannel listener — catches FCM pushes relayed from the SW ────
  // Kept as a secondary path for background pushes (page was not open when
  // the signal fired). Dedup is handled by the notification store (same id).
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;

    const channel = new BroadcastChannel('algoglide-push');
    channel.onmessage = (event) => {
      if (event.data?.type === 'PUSH_NOTIFICATION') {
        handleSignal.current({
          title: event.data.title,
          body: event.data.body,
          url: event.data.url,
          symbol: event.data.symbol,
          direction: event.data.direction,
          confidence: event.data.confidence,
          tag: event.data.tag,
          ai_approved: event.data.ai_approved,
          ai_reasoning: event.data.ai_reasoning,
        });
      }
    };

    return () => channel.close();
  }, [enabled]);

  // ── Re-register FCM when user grants permission from settings modal ────────
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const onGranted = async () => {
      if (Notification.permission !== 'granted') return;
      try {
        const { getFirebaseMessaging, getVapidKey } = await import('@/lib/firebase');
        const { getToken } = await import('firebase/messaging');
        const messaging = getFirebaseMessaging();
        if (!messaging) return;
        let swReg = await navigator.serviceWorker.getRegistration('/');
        if (!swReg) swReg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        const token = await getToken(messaging, { vapidKey: getVapidKey(), serviceWorkerRegistration: swReg });
        if (!token) return;
        await fetch('/api/notifications/register-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        registered.current = true;
      } catch (err) {
        console.warn('[FCM] re-register:', err);
      }
    };

    window.addEventListener('algoglide-request-fcm-register', onGranted);
    return () => window.removeEventListener('algoglide-request-fcm-register', onGranted);
  }, [enabled]);

  // ── FCM token registration ─────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (registered.current) return;
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const setup = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const { getFirebaseMessaging, getVapidKey } = await import('@/lib/firebase');
        const { getToken } = await import('firebase/messaging');

        const messaging = getFirebaseMessaging();
        if (!messaging) return;

        // Reuse the existing PWA service worker (sw.js) — it handles both offline
        // caching and FCM push events. Registering a second SW to the same scope
        // causes a conflict where the FCM SW is stuck in "waiting" and never fires.
        let swRegistration = await navigator.serviceWorker.getRegistration('/');
        if (!swRegistration) {
          swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        }

        // Wait for the SW to become active before requesting a token
        if (!swRegistration.active) {
          await new Promise<void>((resolve) => {
            const sw = swRegistration!.installing ?? swRegistration!.waiting;
            if (!sw) { resolve(); return; }
            sw.addEventListener('statechange', function handler() {
              if ((sw as ServiceWorker).state === 'activated') {
                (sw as ServiceWorker).removeEventListener('statechange', handler);
                resolve();
              }
            });
          });
        }

        const token = await getToken(messaging, {
          vapidKey: getVapidKey(),
          serviceWorkerRegistration: swRegistration,
        });

        if (!token) return;

        await fetch('/api/notifications/register-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        // ── Foreground message handler ─────────────────────────────────
        // When the page is in the foreground, Firebase SDK intercepts
        // the push and fires onMessage instead of the SW push event.
        // Without this handler, foreground signals are silently consumed
        // and no in-app toast or chime fires.
        const { onMessage } = await import('firebase/messaging');
        onMessage(messaging, (payload) => {
          const n = payload.notification || {};
          const d = payload.data || {};
          handleSignal.current({
            title: n.title || d.title,
            body: n.body || d.body,
            url: d.url,
            symbol: d.symbol,
            direction: d.direction,
            confidence: d.confidence,
            tag: d.tag,
          });
        });

        registered.current = true;
      } catch (err) {
        // Non-fatal — push is enhancement, not core feature
        console.warn('[FCM]', err);
      }
    };

    setup();
  }, [enabled]);
}
